import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useParams } from "wouter";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import MyContainer from "@/components/MyContainer";
import { SaveButton } from "@/components/ui/buttons/save-button";
import CustomerForm from "@/pages/customer/form/customer-form";
import type { CustomerFormValues } from "@/api/types";
import { customerSchema } from "@/api/types";
import {
  useCustomer,
  useDeleteCustomer,
  useUpdateCustomer,
} from "@/services/customer-service";
import { useToast } from "@/hooks/use-toast";

export default function EditCustomerPage() {
  const params = useParams();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const customerId = params.id ?? null;

  const { data: customer, isLoading, error } = useCustomer(customerId);
  const updateCustomerMutation = useUpdateCustomer();
  const deleteCustomerMutation = useDeleteCustomer();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const defaultValues = useMemo<CustomerFormValues>(
    () => ({
      name: customer?.name ?? "",
      phone: customer?.phone ?? "",
      address: customer?.address ?? "",
      saleChannel: customer?.saleChannel ?? "Facebook",
      notTransfer: customer?.notTransfer ?? false,
    }),
    [customer]
  );

  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(customerSchema),
    mode: "onChange",
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  const handleSubmit = async (values: CustomerFormValues) => {

    if (!customerId) {
      throw new Error("Customer id is required");
    }

    const response = await updateCustomerMutation.execute({
      id: customerId,
      data: values,
    });

    if (!response.success || !response.data) {
      throw new Error(response.message || "Failed to update customer");
    }
    toast({
      title: "Customer updated",
      description: "The customer has been updated successfully.",
    });


    return response.data;
  };

  const handleSuccess = () => {

    setLocation("/customers");
  };

  const handleCancel = () => {
    setLocation("/customers");
  };

  const handleSave = () => {
    form.handleSubmit(handleSubmit)();
  };

  const handleDelete = async () => {
    if (!customerId) {
      return;
    }

    setIsDeleting(true);

    try {
      const response = await deleteCustomerMutation.execute(customerId);

      if (!response.success) {
        throw new Error(response.message || "Failed to delete customer");
      }

      toast({
        title: "Customer deleted",
        description: "The customer has been removed successfully.",
      });

      setIsDeleteDialogOpen(false);
      setLocation("/customers");
    } catch (err: any) {
      toast({
        title: "Delete failed",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          height: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !customer || !customerId) {
    return (
      <MyContainer
        title="Edit customer"
        subtitle="Unable to load customer details."
        actions={
          <Button variant="contained" onClick={() => setLocation("/customers")}>
            Back to list
          </Button>
        }
      >
        <Alert severity="error">
          <AlertTitle>Unable to load customer</AlertTitle>
          {error?.message || "Please try again later."}
        </Alert>
      </MyContainer>
    );
  }

  return (
    <>
      <MyContainer
        title={`Edit ${customer.name}`}
        subtitle="Update customer information and preferences."
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setIsDeleteDialogOpen(true)}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
            <SaveButton
              disabled={form.formState.isSubmitting}
              handleSave={handleSave}
            />
          </Stack>
        }
        footer={
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
              <SaveButton
                disabled={form.formState.isSubmitting}
                handleSave={handleSave}
              />
            </Stack>
          </Box>
        }
      >
        <CustomerForm
          form={form}
          mode="update"
          onSubmit={handleSubmit}
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      </MyContainer>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        aria-labelledby="delete-customer-title"
      >
        <DialogTitle id="delete-customer-title">Delete customer</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This action cannot be undone. Are you sure you want to delete this
            customer?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
